import { create } from "zustand";
import { useCallback, useState } from "react";
import {
  AutocompleteGameCardsRequest,
  FinishEnum,
  GameCard,
} from "modules/cards/model/card";
import type { GameId } from "../../common/model/models";
import type { PricingConfiguration } from "modules/common/model/models";

interface CardModalOptions {
  finish?: FinishEnum;
  pricing?: PricingConfiguration;
}

type CardModalState =
  | (CardModalOptions & {
      open: false;
      card: GameCard | null;
      dataId?: string;
      game?: GameId;
    })
  | (CardModalOptions & {
      open: true;
      card: GameCard;
      dataId?: never;
      game: GameId;
    })
  | (CardModalOptions & {
      open: true;
      card: null;
      dataId: string;
      game: GameId;
    });

interface CardsStore {
  // Modal state
  modal: CardModalState;

  // Modal actions
  openModal: (
    card: GameCard,
    pricing?: PricingConfiguration,
    finish?: FinishEnum
  ) => void;
  openModalByDataId: (
    dataId: string,
    game: GameId,
    finish?: FinishEnum,
    pricing?: PricingConfiguration
  ) => void;
  closeModal: () => void;

  // Reset all
  reset: () => void;
}

const DEFAULT_SEARCH_REQUEST: AutocompleteGameCardsRequest = {
  string: "",
  page: 0,
  pageSize: 10,
  foil: false,
  playable: false,
  setCode: "",
  format: "",
  rarity: "",
  types: [],
};

const DEFAULT_MODAL_STATE: CardModalState = {
  open: false,
  card: null,
  dataId: undefined,
  finish: undefined,
};

export const useCardsStore = create<CardsStore>((set) => ({
  // Initial state
  modal: DEFAULT_MODAL_STATE,

  // Modal actions
  openModal: (card, pricing, finish) =>
    set({
      modal: {
        open: true,
        card,
        dataId: undefined,
        game: card.game,
        finish,
        pricing,
      },
    }),

  openModalByDataId: (dataId, game, finish, pricing) =>
    set({
      modal: {
        open: true,
        card: null,
        dataId,
        game,
        finish,
        pricing,
      },
    }),

  closeModal: () =>
    set((state) => ({
      modal: {
        ...state.modal,
        open: false,
      },
    })),

  // Reset all
  reset: () =>
    set({
      modal: DEFAULT_MODAL_STATE,
    }),
}));

// ============================================================================
// SELECTOR HOOKS - Individual pattern to avoid shallow equality issues
// ============================================================================

/**
 * Card modal selector - for modal components
 */
export const useCardModal = () => {
  const modal = useCardsStore((state) => state.modal);
  const openModal = useCardsStore((state) => state.openModal);
  const openModalByDataId = useCardsStore((state) => state.openModalByDataId);
  const closeModal = useCardsStore((state) => state.closeModal);

  return { modal, openModal, openModalByDataId, closeModal };
};

/**
 * Surface-local card search state.
 *
 * Autocomplete request state is intentionally not kept in the global card
 * store: the app bar, search overlay, and page-level autocompletes can be
 * mounted at the same time and must not populate each other's results.
 */
export const useCardSearch = () => {
  const [searchRequest, setRequest] = useState(DEFAULT_SEARCH_REQUEST);

  const setSearchRequest = useCallback(
    (req: Partial<AutocompleteGameCardsRequest>) => {
      setRequest((current) => ({ ...current, ...req }));
    },
    []
  );

  const resetSearchRequest = useCallback(() => {
    setRequest(DEFAULT_SEARCH_REQUEST);
  }, []);

  return { searchRequest, setSearchRequest, resetSearchRequest };
};
